import React, { useCallback, memo } from "react";
import {
  Box,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  MenuItem,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIndicatorIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  PropertyDetailsType,
  PropertyDetailsSize,
  type PropertyDetails as PropertyDetail,
} from "../../interfaces/property";

export interface SortableDetailProps {
  id: string;
  index: number;
  detail: PropertyDetail;
  isAnyDragging: boolean;
  onUpdate: (index: number, field: keyof PropertyDetail, value: any) => void;
  onRemove: (index: number) => void;
  renderEditor: (index: number) => React.ReactNode;
}

const SortableDetailComponent: React.FC<SortableDetailProps> = ({ 
  id, 
  index, 
  detail, 
  isAnyDragging,
  onUpdate, 
  onRemove,
  renderEditor 
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: 'grab',
  } as React.CSSProperties;

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(index, "name", e.target.value);
  }, [index, onUpdate]);

  const handleTypeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(index, "type", e.target.value);
  }, [index, onUpdate]);

  const handleSizeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onUpdate(index, "size", e.target.value);
  }, [index, onUpdate]);

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove(index);
  }, [index, onRemove]);

  return (
    <Accordion ref={setNodeRef} style={style} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
          <Box
            sx={{ display: "none", alignItems: 'center', color: 'text.secondary', cursor: 'grab' }}
            {...attributes}
            {...listeners}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <TextField
            size="small"
            value={detail.name}
            onChange={handleNameChange}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ minWidth: 200, flex: 1 }}
          />
          <Box sx={{ display: "none", alignItems: "center", gap: 1 }}>
            <TextField
              select
              size="small"
              value={detail.type}
              onChange={handleTypeChange}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ minWidth: 160 }}
            >
              {Object.values(PropertyDetailsType).map((typeOption) => (
                <MenuItem key={typeOption} value={typeOption}>
                  {typeOption}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={detail.size}
              onChange={handleSizeChange}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ minWidth: 120 }}
            >
              {Object.values(PropertyDetailsSize).map((sizeOption) => (
                <MenuItem key={sizeOption} value={sizeOption}>
                  {sizeOption}
                </MenuItem>
              ))}
            </TextField>
            <IconButton size="small" color="error" onClick={handleRemove}>
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: isAnyDragging ? 'none' : 'block' }}>
        {!isAnyDragging && renderEditor(index)}
      </AccordionDetails>
    </Accordion>
  );
};

export const SortableDetail = memo(SortableDetailComponent);
